import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  /**
   * Keep drawer strictly mobile-only.
   * Theme breakpoint: mobile < 750, tablet/desktop >= 750
   */
  static DESKTOP_BREAKPOINT = 750;

  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Toggle overflow-list collapsing for desktop/tablet.
   * Desktop/tablet should always keep full menu (no "More" collapse state).
   * @param {boolean} disabled
   */
  #setOverflowDisabled(disabled) {
    const overflowList = this.refs.headerMenu?.querySelector('overflow-list');
    if (!overflowList) return;

    if (disabled) {
      overflowList.setAttribute('disabled', 'true');
    } else {
      overflowList.removeAttribute('disabled');
    }
  }

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Returns the main page scroll position for the current document.
   * This is the canonical source for sticky header direction.
   * @returns {number}
   */
  #getPageScrollTop = () =>
    document.scrollingElement?.scrollTop ||
    document.documentElement?.scrollTop ||
    document.body?.scrollTop ||
    window.scrollY ||
    0;

  /**
   * Resolve current scroll top across storefront + Theme Editor preview containers.
   * @param {Event | undefined} event
   * @returns {number}
   */
  #getScrollTop = (event) => {
    const pageScrollTop = this.#getPageScrollTop();
    // Theme Editor fallback: also inspect scrollable event targets.
    const eventTarget = event?.target;
    let containerScrollTop = null;
    if (eventTarget instanceof Element && typeof eventTarget.scrollTop === 'number') {
      if (eventTarget.scrollHeight > eventTarget.clientHeight + 1) {
        containerScrollTop = eventTarget.scrollTop || 0;
      } else {
        const ancestor = eventTarget.closest('*');
        if (ancestor instanceof Element) {
          let node = ancestor;
          while (node && node !== document.body && node !== document.documentElement) {
            if (node.scrollHeight > node.clientHeight + 1) {
              const style = window.getComputedStyle(node);
              if (/(auto|scroll|overlay)/.test(style.overflowY)) {
                containerScrollTop = node.scrollTop || 0;
                break;
              }
            }
            node = node.parentElement;
          }
        }
      }
    }
    if (containerScrollTop == null) return pageScrollTop;
    if (pageScrollTop === 0) return containerScrollTop;

    // Pick the source that actually moved relative to our previous frame.
    const pageDelta = Math.abs(pageScrollTop - this.#lastScrollTop);
    const containerDelta = Math.abs(containerScrollTop - this.#lastScrollTop);
    return containerDelta > pageDelta ? containerScrollTop : pageScrollTop;
  };

  /**
   * Applies sticky state transitions for sticky='scroll-up' mode.
   * @param {'up' | 'down' | 'none'} direction
   * @param {number} scrollTop
   */
  #applyScrollUpState = (direction, scrollTop) => {
    const isAtTop = scrollTop <= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (isAtTop) {
      this.#offscreen = false;
      this.removeAttribute('data-animating');
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      this.style.setProperty('opacity', '1', 'important');
      return;
    }

    if (direction === 'up') {
      this.removeAttribute('data-animating');
      this.#offscreen = true;
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
      this.style.setProperty('opacity', '1', 'important');
      return;
    }

    if (this.dataset.stickyState === 'active') {
      this.#offscreen = true;
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');
      this.style.setProperty('opacity', '0', 'important');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
      return;
    }

    this.#offscreen = true;
    this.dataset.scrollDirection = 'none';
    this.dataset.stickyState = 'idle';
    this.style.setProperty('opacity', '0', 'important');
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Tablet/Desktop: always force full menu mode (never drawer/hamburger).
    if (window.innerWidth >= HeaderComponent.DESKTOP_BREAKPOINT) {
      this.#setOverflowDisabled(true);
      this.#updateMenuVisibility(false);
      return;
    }

    this.#setOverflowDisabled(false);

    // Mobile fallback: if width grows after a mobile-drawer state, restore menu.
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    // Never allow overflow logic to enable drawer on tablet/desktop.
    if (window.innerWidth >= HeaderComponent.DESKTOP_BREAKPOINT) {
      this.#setOverflowDisabled(true);
      this.#updateMenuVisibility(false);
      return;
    }

    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = (event) => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = this.#getScrollTop(event);
    if (!stickyMode) return;

    const delta = scrollTop - this.#lastScrollTop;
    const absDelta = Math.abs(delta);
    const isScrollingUp = delta < 0;
    const isAtTop = scrollTop <= 0;
    const directionThreshold = 2;

    // Ignore tiny oscillations that can happen in editor previews.
    if (!isAtTop && absDelta < directionThreshold) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (stickyMode === 'always') {
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // Deterministic "on scroll up" behavior:
    // - at top -> show
    // - scrolling up -> show
    // - scrolling down -> hide
    this.#applyScrollUpState(isScrollingUp ? 'up' : 'down', scrollTop);

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Fallback direction detector for environments where scrollTop deltas are unreliable
   * (notably Shopify Theme Editor preview containers).
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    if (this.getAttribute('sticky') !== 'scroll-up') return;

    const scrollTop = this.#getScrollTop(event);
    let direction = 'none';
    if (event.deltaY < 0) direction = 'up';
    if (event.deltaY > 0) direction = 'down';
    if (direction === 'none') return;

    this.#applyScrollUpState(direction, scrollTop);
    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#lastScrollTop = this.#getScrollTop();
    this.#setOverflowDisabled(window.innerWidth >= HeaderComponent.DESKTOP_BREAKPOINT);
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      // For "always" mode, keep observer behavior.
      // For "scroll-up", use deterministic direction logic only.
      if (stickyMode === 'always') {
        this.#observeStickyPosition(true);
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        // Capture scroll events from document and nested scroll containers (Theme Editor preview).
        document.addEventListener('scroll', this.#handleWindowScroll, true);
        window.addEventListener('scroll', this.#handleWindowScroll);
        document.addEventListener('wheel', this.#handleWheel, { capture: true, passive: true });
      }
      if (stickyMode === 'scroll-up') {
        // Initialize visible state to match expected behavior immediately.
        this.#applyScrollUpState('none', this.#lastScrollTop);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll, true);
    window.removeEventListener('scroll', this.#handleWindowScroll);
    document.removeEventListener('wheel', this.#handleWheel, true);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

